export interface Competency {
    id: string;
    name: string;
    assessedScore: number;
}

export interface CompetencyGroup {
    id: string;
    name: string;
    competencies: Competency[];
}

/**
 * Sorts competency groups in "sandwich order":
 * - Start with highest-rated groups
 * - Place lowest-rated groups in the middle
 * - End with highest-rated groups
 *
 * Example: [4.0, 3.5, 3.0, 2.0, 1.0] → [4.0, 3.0, 1.0, 2.0, 3.5]
 */
export function sandwichSortCompetencyGroups(groups: CompetencyGroup[]): CompetencyGroup[] {
    if (groups.length === 0) {
        return [];
    }

    if (groups.length === 1) {
        return [...groups];
    }

    // 1. Calculate average score per group
    const ratedGroups = groups.map((group) => {
        const totalScore = group.competencies.reduce((sum, c) => sum + c.assessedScore, 0);
        const averageScore =
            group.competencies.length > 0 ? totalScore / group.competencies.length : 0;
        return { group, averageScore };
    });

    const baselineScore = ratedGroups[0].averageScore;
    const allEqual = ratedGroups.every(
        (ratedGroup) => Math.abs(ratedGroup.averageScore - baselineScore) <= 0.001,
    );

    if (allEqual) {
        return [...groups].sort((a, b) => a.name.localeCompare(b.name));
    }

    // 2. Sort descending by average score
    ratedGroups.sort((a, b) => {
        if (Math.abs(a.averageScore - b.averageScore) > 0.001) {
            return b.averageScore - a.averageScore;
        }
        return a.group.name.localeCompare(b.group.name);
    });

    // 3. Distribute in sandwich pattern
    const result: CompetencyGroup[] = new Array(groups.length);
    let left = 0;
    let right = groups.length - 1;

    for (let i = 0; i < ratedGroups.length; i++) {
        if (i % 2 === 0) {
            result[left] = ratedGroups[i].group;
            left++;
        } else {
            result[right] = ratedGroups[i].group;
            right--;
        }
    }

    return result;
}
